import React, { createContext, useContext, useState, useEffect } from 'react'; // Import React and hooks needed for context and state management
import { auth } from './firebase'; // Import the Firebase auth object from the firebase configuration file
import { onAuthStateChanged } from 'firebase/auth'; // Import onAuthStateChanged for listening to changes in authentication state

// Create a context for authentication to store and provide authentication state
const AuthContext = createContext();

// Custom hook to use the AuthContext, providing easy access to the authentication state throughout the application
export const useAuth = () => {
  return useContext(AuthContext); // Return the value of AuthContext, which contains the current user's authentication state
};

// AuthProvider component that wraps around parts of the application needing authentication state
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null); // State to hold the currently authenticated user, initially set to null
  const [loading, setLoading] = useState(true); // State to track whether the app is still loading the user's authentication state

  // Effect hook to handle Firebase authentication state changes when the component mounts
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("Auth state changed:", user); // Debugging: log any changes in the auth state
      setCurrentUser(user); // Update the state with the currently authenticated user (or null if logged out)
      setLoading(false); // Set loading to false once the auth state has been resolved
    });

    // Cleanup function: unsubscribe from the onAuthStateChanged listener when the component unmounts
    return () => unsubscribe();
  }, []); // Empty dependency array means this effect runs only once after the initial render

  // Value to be provided by the AuthContext, containing the currentUser state
  const value = {
    currentUser, // Expose currentUser to any component that consumes this context
  };

  return (
    <AuthContext.Provider value={value}> 
      {/* Only render children components when not loading */}
      {!loading && children} 
    </AuthContext.Provider>
  );
};
